import { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { IAPIGameItem } from '../../../model/api-modules';
import { useHistory } from 'react-router-dom';
import { GameCard } from './game-card';
import { Spin } from 'antd';
import { CreateGameModal } from '../../component/create-game-modal';
import { fetchGameList } from '../../../api';
import { IComponentProps } from '../../../model/studio';
import SelectStudioIngame from '../../../assets/img/select-studio-ingame.png';
import StudioCartoonFigure from '../../../assets/img/studio-cartoon-figure.png';
import SmilingFace from '../../../assets/img/smiling-face.png';
import { DocumentationCard } from './documentation-card';
import { getConfig } from '../../../config';
import getFormatMessage from '../../../locales/get-format-message';
import { getLocaleLanguage, LanguageType } from '../../../lib';
import { wrapTimeReport } from '../../lib/bk-report';

export const PortalContentComponent = (props: IComponentProps) => {
  const history = useHistory();
  const [showCreateGameModal, setShowCreateGameModal] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [gameList, setGameList] = useState<ReadonlyArray<IAPIGameItem>>([]);

  const studioID = props.studioInfo.studio.StudioID;
  const currentStoreLanguage = getLocaleLanguage() || LanguageType.en_US;
  const { portalLastVersionUpdateTime } = getConfig();
  const versionUpdateTime = portalLastVersionUpdateTime[currentStoreLanguage];

  useEffect(() => {
    wrapTimeReport(initialization);
  }, []);

  const initialization = async () => {
    setLoading(true);
    await queryGameList();
    setLoading(false);
  };

  const queryGameList = async () => {
    const res = await fetchGameList();
    setGameList(res || []);
  };

  const renderGameCards = () => {
    const cards = gameList.filter((v) => v.studioID === studioID && v.gameID);
    return (
      <div className={styles.gameCards}>
        <div className={styles.cardWrapper}>
          {cards.map(
            (game, index) =>
              index !== 0 && <GameCard key={game.gameID} info={game} />
          )}
        </div>
      </div>
    );
  };

  const renderAddGameCard = () => {
    if (showCreateGameModal) {
      return (
        <CreateGameModal
          onReload={queryGameList}
          onClosed={() => setShowCreateGameModal(false)}
        />
      );
    }
    return null;
  };

  const renderHeader = () => {
    const cards = gameList.filter((v) => v.studioID === studioID && v.gameID);
    return (
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerTitle}>
            {getFormatMessage('my_studios_portal')}
          </div>
          <div className={styles.headerUpdate}>
            {getFormatMessage('last_update')}
            {versionUpdateTime}
          </div>
          <div className={styles.headerContent}>
            {getFormatMessage('services_focus_innovation')}
            <br />
            {getFormatMessage('services_focus_innovation_cn')}
          </div>
          <div
            className={styles.headerDownloadCenterBtn}
            onClick={() =>
              history.push(`/download/download-center?studioId=${studioID}`)
            }
          >
            {getFormatMessage('download_center')}
          </div>
          <div className={styles.headerGameCardBox}>
            <GameCard
              disabled={!props.isOwner}
              addItem={true}
              addItemClick={() => setShowCreateGameModal(true)}
            />
            {cards.map(
              (game, index) =>
                index === 0 && <GameCard key={game.gameID} info={game} />
            )}
          </div>
        </div>
        <div className={styles.headerRight}>
          <img src={SelectStudioIngame} className={styles.ingameImg} />
          <img src={StudioCartoonFigure} className={styles.figureImg} />
          <img src={SmilingFace} className={styles.faceImg} />
        </div>
      </div>
    );
  };

  const renderDocumentation = () => {
    // 分开两行布局
    // const documentationCards = [
    //   [
    //     {
    //       title: 'InGame Services',
    //       content: getFormatMessage('doc_in_game_services_content'),
    //     },
    //     {
    //       title: 'Sample Demo Tutorials',
    //       content: getFormatMessage('sample_demo_tutorials_content'),
    //     },
    //   ],
    //   [
    //     {
    //       title: 'SDKs & Tools',
    //       content: getFormatMessage('sdks_tools_content'),
    //     },
    //     {
    //       title: 'Knowledge Base',
    //       content: getFormatMessage('knowledge_base_content'),
    //     },
    //   ],
    // ];
    const config = getConfig().documentConfig;
    const { consoleHelperLinks } = config;
    const linksTopTwo = consoleHelperLinks[currentStoreLanguage].slice(1, 3); // 获取第2个到第3个对象
    const linksBottomTwo = consoleHelperLinks[currentStoreLanguage].slice(3, 5); // 获取第4个到第5个对象
    return (
      <div className={styles.documentationBox}>
        <div className={styles.documentationTitle}>
          {getFormatMessage('documentation')}
        </div>
        <div className={styles.documentationContent}>
          {getFormatMessage('get_up_to_speed_steps_desc')}
        </div>
        <div className={styles.documentationCardBox}>
          {linksTopTwo.map((link, index) => (
            <DocumentationCard
              key={index}
              title={link.label}
              content={link.desc || ''}
              url={
                link.url.indexOf(getConfig().consoleURL) !== -1
                  ? link.url
                  : `${getConfig().consoleURL}${link.url}`
              }
            />
          ))}
        </div>
        <div className={styles.documentationCardBox}>
          {linksBottomTwo.map((link, index) => (
            <DocumentationCard
              key={index}
              title={link.label}
              content={link.desc || ''}
              url={
                link.url.indexOf(getConfig().consoleURL) !== -1
                  ? link.url
                  : `${getConfig().consoleURL}${link.url}`
              }
            />
          ))}
        </div>
      </div>
    );
  };

  const renderLearnMore = () => {
    const learnMoreCard = {
      title: getFormatMessage('get_started_title'),
      content: getFormatMessage('get_started_content'),
      url: `${getConfig().websiteURL}/?ingame_language=${currentStoreLanguage}`,
    };
    return (
      <div className={styles.learnMoreBox}>
        <div className={styles.learnMoreBgBox}>
          <div className={styles.learnMoreLeft}>
            <div className={styles.learnMoreTitle}>{learnMoreCard.title}</div>
            <div className={styles.learnMoreContent}>
              {learnMoreCard.content}
            </div>
          </div>
          <div
            className={styles.learnMoreRight}
            onClick={() =>
              window.open(learnMoreCard.url ? learnMoreCard.url : '', undefined)
            }
          >
            {getFormatMessage('learn_more')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Spin spinning={loading} style={{ flex: 1 }}>
      {renderHeader()}
      {!loading ? renderGameCards() : null}
      {renderDocumentation()}
      {renderLearnMore()}
      {renderAddGameCard()}
    </Spin>
  );
};
